import { Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { SidebarWorkflowComponent } from '../sidebar-workflow/sidebar-workflow.component';

const PROJECT_GLYPH_PALETTE = [
  '#E36F2A',
  '#3D7D5A',
  '#6B6A60',
  '#A23B2E',
  '#3A5A7A',
  '#B8772A',
];

interface SidebarWorkflowVm {
  id: string;
  name: string;
  route: string;
  icon: string;
  colorHex: string;
  isActive: boolean;
}

export interface SidebarProjectVm {
  id: string;
  name: string;
  initials: string;
  coverImageUrl?: string | null;
  iconKey?: string | null;
  colorHex?: string | null;
  route: string;
  isActive: boolean;
  workflows: SidebarWorkflowVm[];
}

@Component({
  selector: 'app-sidebar-project',
  standalone: true,
  imports: [RouterLink, LucideAngularModule, SidebarWorkflowComponent],
  templateUrl: './sidebar-project.component.html',
  styleUrl: './sidebar-project.component.css',
})
export class SidebarProjectComponent {
  readonly project = input.required<SidebarProjectVm>();
  readonly expanded = input<boolean>(false);

  readonly toggleProject = output<string>();
  readonly createWorkflow = output<SidebarProjectVm>();

  /**
   * Cor do glyph: respeita `colorHex` escolhido pelo usuário (settings → ícone + cor).
   * Sem colorHex, cai num hash determinístico sobre o id pra ficar estável entre reloads.
   */
  readonly glyphColor = computed<string>(() => {
    const chosen = (this.project().colorHex ?? '').trim();
    if (/^#[0-9A-Fa-f]{6}$/i.test(chosen)) return chosen;
    const id = this.project().id ?? '';
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = (hash * 31 + id.charCodeAt(i)) | 0;
    }
    const idx = Math.abs(hash) % PROJECT_GLYPH_PALETTE.length;
    return PROJECT_GLYPH_PALETTE[idx];
  });

  /**
   * Cor do glifo (ícone/iniciais) por luminância do fundo — mesma lógica do
   * `resolveAccentForeground` da lista de projetos. Fundo claro → texto/ícone
   * escuro; fundo escuro → branco. Sem isso, ícone em verde-claro vira invisível.
   */
  readonly glyphForeground = computed<string>(() => {
    const bg = this.glyphColor().replace('#', '');
    const red = Number.parseInt(bg.slice(0, 2), 16);
    const green = Number.parseInt(bg.slice(2, 4), 16);
    const blue = Number.parseInt(bg.slice(4, 6), 16);
    const luminance = (red * 299 + green * 587 + blue * 114) / 1000;
    return luminance > 150 ? '#111827' : '#FFFFFF';
  });
}
